import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { adminApi } from "@/api/admin.ts";
import type { ApiError } from "@/api/client.ts";

const DEFAULT_SETTINGS = [
  { key: "site.name", description: "Site display name" },
  { key: "site.tagline", description: "Homepage tagline" },
  { key: "site.maintenance_mode", description: "Enable maintenance mode (true/false)" },
  { key: "billing.trial_credits", description: "Credits given to new users on signup" },
  { key: "billing.currency", description: "Default billing currency (ISO code)" },
  { key: "render.max_concurrent", description: "Max concurrent renders per user" },
  { key: "render.max_video_length_override", description: "Override max video length in seconds (0 = use plan default)" },
  { key: "email.from_address", description: "System email sender address" },
  { key: "email.support_address", description: "Support email address" },
];

export function AdminSettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => adminApi.getSettings(),
  });

  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  // Populate from server data
  useEffect(() => {
    if (data?.settings) {
      const initial: Record<string, string> = {};
      for (const [key, { value }] of Object.entries(data.settings)) {
        initial[key] = value;
      }
      // Also add defaults that don't exist yet
      for (const def of DEFAULT_SETTINGS) {
        if (!(def.key in initial)) {
          initial[def.key] = "";
        }
      }
      setEditedValues(initial);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      // Only send changed values
      const changes: Record<string, string> = {};
      for (const [key, value] of Object.entries(editedValues)) {
        const original = data?.settings[key]?.value;
        if (value !== original && value !== "") {
          changes[key] = value;
        }
      }
      return adminApi.updateSettings(changes);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "settings"] }),
  });

  const handleAddCustom = () => {
    if (newKey.trim() && newValue.trim()) {
      setEditedValues({ ...editedValues, [newKey.trim()]: newValue.trim() });
      setNewKey("");
      setNewValue("");
    }
  };

  const error = save.error as ApiError | null;
  const allKeys = Object.keys(editedValues).sort();

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-fg">Site Settings</h1>
          <p className="mt-1 text-fgMuted">Global configuration for the platform</p>
        </div>
        <Button loading={save.isPending} onClick={() => save.mutate()}>
          <Save className="h-4 w-4" /> Save Changes
        </Button>
      </div>

      {error && <p className="mb-4 text-sm text-danger">{error.message}</p>}
      {save.isSuccess && <p className="mb-4 text-sm text-success">Settings saved</p>}

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <>
          <Card className="mb-6">
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {allKeys.map((key) => {
                  const def = DEFAULT_SETTINGS.find((d) => d.key === key);
                  const serverVal = data?.settings[key];
                  const isModified = editedValues[key] !== (serverVal?.value ?? "");
                  return (
                    <div key={key} className="flex items-start gap-4 px-6 py-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-sm font-mono font-medium text-fg">{key}</code>
                          {isModified && <Badge variant="warning">Modified</Badge>}
                        </div>
                        {def?.description && <p className="text-xs text-fgMuted mb-2">{def.description}</p>}
                      </div>
                      <div className="w-72">
                        <Input
                          value={editedValues[key] ?? ""}
                          onChange={(e) => setEditedValues({ ...editedValues, [key]: e.target.value })}
                          placeholder="(not set)"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Add custom setting */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Add Custom Setting</CardTitle>
              <CardDescription>Add a new key/value pair to the site configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Input label="Key" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="e.g. feature.beta_enabled" />
                </div>
                <div className="flex-1">
                  <Input label="Value" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="true" />
                </div>
                <Button variant="outline" onClick={handleAddCustom} disabled={!newKey.trim()}>
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
